<template>
  <div class="complete-the-look critical-component-clear container">
    <h2
      v-if="liquid.sectionTitle"
      class="complete-the-look__title text-heading-2xs text-heading-s-desktop"
      v-text="liquid.sectionTitle"
    />
    <div class="grid complete-the-look__grid-layout">
      <div
        v-if="liquid.image"
        class="complete-the-look__image col l7 xs6"
      >
        <responsive-image
          :image="liquid.image"
          min-max="300-1000"
          ratio="3:4"
          sizes="100vw"
          :lazyload="processedLazyloadValue"
        />
      </div>
      <div class="complete-the-look__content col l5 xs6">
        <div class="complete-the-look__right--content--wrapper">
          <div class="complete-the-look__content-wrapper">
            <p
              v-if="liquid.title"
              class="
                complete-the-look__content--title
                text-heading-s
                text-heading-m-desktop
              "
              v-text="liquid.title"
            />
            <div class="complete-the-look__top-price-wrapper">
              <span
                class="text-body-subtitle-xs text-body-subtitle-s-desktop"
                v-text="$string('general.complete_the_look.total')"
              />
              <product-prices
                class="text-body-subtitle-xs text-body-subtitle-s-desktop complete-the-look__prices"
                :price="total"
                :compare-at-price="totalCompareAtPrice > total ? totalCompareAtPrice : false"
                :varies="varies"
                block-class="complete-the-look-prices"
                element="div"
              />
            </div>
          </div>
          <div
            v-if="liquid.products.length"
            class="complete-the-look__products"
          >
            <template
              v-for="(product, index) of liquid.products"
              :key="product.id"
            >
              <div
                v-if="product.available"
                class="complete-the-look__product-card"
              >
                <div class="complete-the-look__product-item grid">
                  <div class="complete-the-look__product-image col l4 xs2">
                    <div class="complete-the-look__checkbox-wrapper">
                      <input
                        :id="`checkbox-${product.id}`"
                        type="checkbox"
                        class="complete-the-look__checkbox"
                        :checked="isVarianSelected(product.id) && selectedCheckboxes[product.id]"
                        @change="$event => toggleCheckbox(product.id, $event)"
                      >
                      <responsive-image
                        :image="{ src: product.featured_image }"
                        min-max="300-1000"
                        ratio="3:4"
                        sizes="100vw"
                        :lazyload="index <= 4 && processedLazyloadValue !== 'lazy' ? false : 'lazy'"
                      />
                      <wishlist-button
                        class="complete-the-look__wishlist-button"
                        :product="product"
                        template="card"
                        :variant="{
                          id: isVarianSelected(product.id)
                            ? selectedVariants[product.id]
                            : product.variants[0].id
                        }"
                      />
                    </div>
                  </div>
                  <div class="complete-the-look__product-information col l8 xs4">
                    <div class="complete-the-look__product-content">
                      <a
                        class="complete-the-look__product-detail"
                        :href="`/products/${product.handle}`"
                      >
                        <p
                          class="
                            text-heading-2xs
                            complete-the-look__product-vendor
                          "
                          v-text="product.vendor"
                        />
                        <p
                          class="text-body-xs"
                          v-text="product.title"
                        />
                      </a>
                      <product-prices
                        class="
                          text-heading-2xs
                          complete-the-look__product-price
                        "
                        :price="getSelectedVariantPrice(product, selectedVariants[product.id])"
                        :compare-at-price="compareAtPrice(product, selectedVariants[product.id])"
                        block-class="complete-the-look-product-price"
                        element="div"
                      />
                    </div>
                    <div class="complete-the-look__product-buttons text-body-s form-group">
                      <select
                        :id="`${product.id}-complete-the-look`"
                        class="complete-the-look__variant col l6 xs3"
                        :class="{ 'selected': selectedVariants[product.id] !== '0' }"
                        name="variant_id"
                        @change.prevent="event => handleSelectedVariant(
                          product.id, event.currentTarget.value
                        )"
                      >
                        <option
                          :value="0"
                          :selected="!isVarianSelected(product.id)"
                          v-text="$string('general.complete_the_look.select_size')"
                        />
                        <option
                          v-for="(variant) of product.variants"
                          :key="variant.id"
                          :value="variant.id"
                          :disabled="!variant.available"
                          :selected="selectedVariants[product.id] === variant.id"
                          v-text="variant.option2"
                        />
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <div class="complete-the-look__total">
              <div class="complete-the-look__price-wrapper">
                <span
                  class="text-body-subtitle-xs text-body-subtitle-s-desktop"
                  v-text="$string('general.complete_the_look.total', { replace: { total: '' } })"
                />
                <product-prices
                  class="text-body-subtitle-xs text-body-subtitle-s-desktop"
                  :price="total"
                  :compare-at-price="totalCompareAtPrice > total ? totalCompareAtPrice : false"
                  :varies="varies"
                  block-class="complete-the-look-prices"
                  element="div"
                />
              </div>
              <btn
                class="complete-the-look__total-cta"
                :class="{
                  [$class('disabled')]: addAllDisabled,
                  'active': !addAllDisabled
                }"
                block-class="complete-the-look"
                :label="addAllText"
                modifiers="block"
                :show-icon="false"
                type="button"
                @click="addAllProducts"
              />
            </div>
            <p
              v-if="error"
              class="complete-the-look__error text-body-s text-center"
              v-text="error"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Complete the look (complete-the-look)
 * -----------------------------------------------------------------------------
 * Complete the look section for PDP.
 *
 * @param {String} liquid.sectionTitle - Section's title.
 * @param {String} liquid.title - Outfit title.
 * @param {Boolean|Object} liquid.image - Section's image.
 * @param {Array} liquid.products - Complete the look products.
 * @param {Boolean} [lazyloadValue] - Lazy loading for product images.
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import WishlistButton from '~async/wishlist-button/wishlist-button'
import ProductPrices from '~global/product-prices/product-prices'

export default {
  name: 'CompleteTheLook',

  components: {
    ResponsiveImage,
    Btn,
    WishlistButton,
    ProductPrices,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionTitle: '',
        title: '',
        image: false,
        products: [],
      }),
    },
    lazyloadValue: {
      type: [Boolean],
      default: true,
    },
  },

  data() {
    return {
      total: 0,
      totalCompareAtPrice: 0,
      state: 'ready',
      error: false,
      selectedVariants: {},
      selectedCheckboxes: {},
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Processed lazyload value.
     * Converts `true` to `'lazy'` and keeps other values unchanged.
     * @returns {Boolean|String} Returns 'lazy' if the value is true
     */
    processedLazyloadValue() {
      return this.lazyloadValue ? 'lazy' : this.lazyloadValue
    },

    /**
     * Returns true if no products have variants selected.
     */
    addAllDisabled() {
      // Check if at least one product has a variant selected
      return !Object.values(this.selectedVariants).some(variantId => variantId !== 0)
    },

    /**
     * Returns add all button text based on device and state
     * @returns {String}
     */
    addAllText() {
      if (!this.addAllDisabled) {
        return this.$string('general.complete_the_look.add_all')
      }

      return this.isMobile
        ? this.$string('general.complete_the_look.select_sizes_mobile')
        : this.$string('general.complete_the_look.select_sizes')
    },

    /**
     * Determines if the compare price varies across products
     * @returns {Boolean}
     */
    varies() {
      return this.liquid.products.length > 1 && this.totalCompareAtPrice > 0
    },

    /**
     * Formatted total compare at price with "from" text when needed
     * @returns {String}
     */
    formattedTotalCompareAtPrice() {
      if (this.totalCompareAtPrice > this.total) {
        if (this.varies) {
          return this.$string('product.prices.from', {
            replace: { price: this.$formatMoney(this.totalCompareAtPrice) },
          })
        }
        return this.totalCompareAtPrice
      }
      return false
    },
  },

  mounted() {
    if (!this.liquid.products.length) {
      return
    }

    this.setTotalPrice()
    this.setSelectedVariants()
    this.updateTotalPrice()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Sets total price.
     */
    setTotalPrice() {
      this.total = 0
      this.totalCompareAtPrice = 0

      // Initialize with the first available product's price
      const firstAvailableProduct = this.liquid.products.find(product => product.available)
      if (firstAvailableProduct) {
        const firstProductId = firstAvailableProduct.id
        const firstProductVariantId = this.selectedVariants
          ? this.selectedVariants[firstProductId]
          : 0

        this.total = firstAvailableProduct.price

        // Calculate compare-at-price for the product
        const productCompareAtPrice = this.compareAtPrice(
          firstAvailableProduct,
          firstProductVariantId,
        )
        this.totalCompareAtPrice = productCompareAtPrice || firstAvailableProduct.price
      }
    },

    /**
     * Sets default selected variants.
     */
    setSelectedVariants() {
      this.liquid.products.forEach(product => {
        if (!product.available) {
          return
        }

        if (product.variants.length > 1) {
          this.selectedVariants[product.id] = 0
          this.selectedCheckboxes[product.id] = false

          return
        }

        this.selectedVariants[product.id] = product.variants[0].id
        this.selectedCheckboxes[product.id] = true
      })
    },

    /**
     * Add all products to the cart.
     */
    addAllProducts() {
      this.state = 'adding'

      const items = []

      Object.keys(this.selectedVariants).forEach(key => {
        const variantId = this.selectedVariants[key]

        if (variantId !== 0 && this.selectedCheckboxes[key] === true) {
          items.push({
            id: Number(variantId),
            quantity: 1,
          })
        }
      })

      this.addToCart(items)
    },

    /**
     * Add selected product to the cart.
     * @param {Number} productId - Product id.
     */
    addSingleProduct(productId) {
      this.state = 'adding'
      const variantId = this.selectedVariants[productId]

      if (variantId === 0) {
        return
      }

      const items = [
        {
          id: Number(this.selectedVariants[productId]),
          quantity: 1,
        },
      ]

      this.addToCart(items)
    },

    /**
     * Add items to the cart.
     * @param {Array} items - Selected items.
     */
    async addToCart(items) {
      try {
        await this.addItem(items)
        this.state = 'added'

        if (this.getComponentExists('cart-drawer') && cnvs.page.type !== 'cart') {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        this.state = 'ready'

      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.error = this.$string('general.complete_the_look.error_message')
          return
        }

        reportError('Failed to add product to cart', error)
      }
    },

    /**
     * Handle variant changes.
     * @param {Number} productId - Product id.
     * @param {Number} variantId - Variant id.
     */
    handleSelectedVariant(productId, variantId) {
      this.error = ''

      const numericVariantId = Number(variantId)
      this.selectedVariants[productId] = numericVariantId
      this.selectedCheckboxes[productId] = numericVariantId !== 0
      this.updateTotalPrice()
    },

    /**
     * Returns the price of the selected variant for a given product.
     * @param {Object} product - The product object.
     * @param {Number} variantId - The selected variant ID.
     * @returns {Number} - The price of the selected variant or the
     * product price.
     */
    getSelectedVariantPrice(product, variantId) {
      if (!product || !variantId || variantId === 0) {
        return product.price
      }
      const foundVariant = product.variants.find(variant => variant.id === variantId)
      return foundVariant ? foundVariant.price : product.price
    },

    /**
     * Updates total price based on selected variants and checkboxes.
     */
    updateTotalPrice() {
      this.total = 0
      this.totalCompareAtPrice = 0

      const firstAvailableProduct = this.liquid.products.find(product => product.available)

      if (firstAvailableProduct) {
        const firstProductId = firstAvailableProduct.id

        const anyProductSelected = Object.keys(this.selectedVariants).some(
          key => this.selectedCheckboxes[key] === true && this.selectedVariants[key] !== 0,
        )

        if (!anyProductSelected) {
          const variantId = this.selectedVariants[firstProductId]
          this.total = this.getSelectedVariantPrice(firstAvailableProduct, variantId)
          const productCompareAtPrice = this.compareAtPrice(
            firstAvailableProduct,
            variantId,
          )
          this.totalCompareAtPrice = productCompareAtPrice || this.total
          return
        }
      }

      this.liquid.products.forEach(product => {
        if (!product.available) {
          return
        }

        const productId = product.id
        const variantId = this.selectedVariants[productId]

        if (this.selectedCheckboxes[productId] === true && variantId !== 0) {
          this.total += this.getSelectedVariantPrice(product, variantId)
          const productCompareAtPrice = this.compareAtPrice(product, variantId)
          this.totalCompareAtPrice += productCompareAtPrice ||
            this.getSelectedVariantPrice(product, variantId)
        }
      })
    },

    /**
     * Returns true if variant selected.
     * @param {Number} productId - Product id.
     */
    isVarianSelected(productId) {
      return this.selectedVariants[productId] !== 0
    },

    /**
     * Toggle checkbox selection for a product
     * @param {Number} productId - Product id
     * @param {Event} event - Change event object
     */
    toggleCheckbox(productId, event) {
      this.error = ''

      if (!this.isVarianSelected(productId)) {
        event.preventDefault()
        event.target.checked = false
        this.selectedCheckboxes[productId] = false

        this.focusVariantDropdown(productId)
        return
      }

      const isChecked = event.target.checked
      this.selectedCheckboxes[productId] = isChecked

      if (!isChecked) {
        this.selectedVariants[productId] = 0
      }

      this.updateTotalPrice()
    },

    /**
     * Compute compare at price to display for a specific product
     * @param {Object} product - Product object
     * @param {Object|Number} variantId - Variant ID or variant object
     * @returns {Number}
     */
    compareAtPrice(product, variantId) {
      if (!product) {
        return 0
      }

      if (variantId && variantId !== 0) {
        const foundVariant = product.variants.find(item => item.id === variantId)
        return foundVariant ? foundVariant.compare_at_price : 0
      }

      return product.compare_at_price || 0
    },

    /**
     * Focus and expand the variant dropdown
     * @param {Number} productId - Product id
     */
    focusVariantDropdown(productId) {
      const selectElement = document.getElementById(`${productId}-complete-the-look`)
      if (selectElement) {
        selectElement.focus()
        if (typeof selectElement.showPicker === 'function') {
          selectElement.showPicker()
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import './complete-the-look';
</style>
